import React from "react";
import { 
  ArrowLeft, 
  ChevronLeft, 
  Factory, 
  HardHat, 
  Package, 
  Search, 
  ShieldCheck, 
  Star, 
  Store, 
  Truck, 
  Wrench, 
  Settings, 
  PenTool, 
  Cpu,
  Cog,
  CheckCircle2, 
  ChevronRight,
  ArrowRight,
  Monitor,
  Dna,
  Zap,
  Globe,
  Droplet,
  PaintBucket,
  Layers,
  Shield,
  Gem,
  Mountain,
  Hammer,
  MessageSquare,
  Clock
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Product, Post, SiteSettings } from "../types";
import ProductCard from "./ProductCard";
import { getOptimizedImageUrl } from "../lib/supabase";

interface AlmasLandingProps {
  setCurrentTab: (tab: string) => void;
  settings: SiteSettings;
  products: Product[];
  posts: Post[];
  currency: string;
  onAddToQuote: (product: Product) => void;
  quoteCart: any[];
  setSelectedCategory: (cat: string) => void;
  appLanguage: "fa" | "en";
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function AlmasLanding({
  setCurrentTab,
  settings,
  products,
  posts,
  currency,
  onAddToQuote,
  quoteCart,
  setSelectedCategory,
  appLanguage,
  searchQuery,
  setSearchQuery,
}: AlmasLandingProps) {
  const isEn = appLanguage === "en";
  
  // Logic for Product Filtering
  const featuredProducts = products.filter(p => p.isFeatured);
  const recentProducts = [...products].sort((a, b) => new Date(b.id).getTime() - new Date(a.id).getTime()).slice(0, 8);
  
  // Dynamic Categories from Settings
  const homeCategories = React.useMemo(() => {
    return settings.featuredCategories || [];
  }, [settings.featuredCategories]);

  const navToStore = (cat?: string) => {
    if (cat) setSelectedCategory(cat);
    else setSelectedCategory("همه");
    setCurrentTab("store");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 1. HERO SLIDER LOGIC
  const sliderImages = settings.homeHeroSlides && settings.homeHeroSlides.length > 0 
    ? settings.homeHeroSlides 
    : [
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=75&w=1200&fm=webp",
        "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=75&w=1200&fm=webp",
        "https://images.unsplash.com/photo-1580983218765-f663ca01f44a?q=75&w=1200&fm=webp"
      ];
  
  const [currentSlide, setCurrentSlide] = React.useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  const [isSearchFocused, setIsSearchFocused] = React.useState(false);
  const searchResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.specs?.nameEn && p.specs.nameEn.toLowerCase().includes(q)) ||
        p.sku.toLowerCase().includes(q) ||
        p.category.some(c => 
          c.toLowerCase().includes(q) || 
          (settings.categoriesEn?.[c] && settings.categoriesEn[c].toLowerCase().includes(q))
        ) ||
        (p.specs?.brand && (
          p.specs.brand.toLowerCase().includes(q) ||
          (settings.brandsEn?.[p.specs.brand] && settings.brandsEn[p.specs.brand].toLowerCase().includes(q))
        ))
    ).slice(0, 5);
  }, [searchQuery, products]);

  // Icons Helper for Categories
  const getCategoryIcon = (catName: string) => {
    const iconName = settings.categoryIcons?.[catName] || "Package";
    const iconProps = { className: "w-6 h-6" };
    
    switch(iconName) {
      case "Factory": return <Factory {...iconProps} />;
      case "Wrench": return <Wrench {...iconProps} />;
      case "Settings": return <Settings {...iconProps} />;
      case "Cog": return <Cog {...iconProps} />;
      case "Cpu": return <Cpu {...iconProps} />;
      case "Truck": return <Truck {...iconProps} />;
      case "Zap": return <Zap {...iconProps} />;
      case "Droplet": return <Droplet {...iconProps} />;
      case "PaintBucket": return <PaintBucket {...iconProps} />;
      case "Layers": return <Layers {...iconProps} />;
      case "Shield": return <Shield {...iconProps} />;
      case "Gem": return <Gem {...iconProps} />;
      case "Mountain": return <Mountain {...iconProps} />;
      case "Hammer": return <Hammer {...iconProps} />;
      default: return <Package {...iconProps} />;
    }
  };

  return (
    <div className="w-full flex flex-col bg-white overflow-hidden" style={{ direction: isEn ? "ltr" : "rtl" }}>
      
      {/* SECTION 1: DYNAMIC CINEMATIC HERO */}
      <section className="relative w-full h-[90vh] flex items-center bg-[#060d1a]">
        {/* Background Slider - Keep overflow hidden here to contain images */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img 
              key={currentSlide}
              src={getOptimizedImageUrl(sliderImages[currentSlide], 1400)} 
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover"
              alt="Almas Machinery"
              // @ts-ignore
              fetchPriority={currentSlide === 0 ? "high" : "auto"}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-[#060d1a] via-[#060d1a]/60 to-transparent z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060d1a] via-transparent to-transparent z-10" />
          
          {/* Animated Glows */}
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-600/20 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-cyan-500/10 blur-[120px] rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto w-full px-6 relative z-40">
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl space-y-8"
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-cyan-400 text-xs font-black tracking-widest backdrop-blur-xl">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              {isEn 
                ? (settings.homeHeroBadgeEn || "TRUSTED INDUSTRIAL PARTNER") 
                : (settings.homeHeroBadge || "تأمین‌کننده معتبر و دانش‌بنیان صنعتی")}
            </div>

            <h1 
              className="font-black text-white leading-[1.2] md:leading-[1.3] tracking-tighter"
              style={{ fontSize: settings.homeHeroTitleSize ? `clamp(32px, 8vw, ${settings.homeHeroTitleSize}px)` : 'clamp(36px, 10vw, 80px)' }}
            >
              {isEn ? (settings.homeHeroTitleEn || "Advanced") : (settings.homeHeroTitle || "تأمین‌کننده پیشرفته")} <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400">
                {isEn ? (settings.homeHeroTitleGradientEn || "Stone & Mining Tech") : (settings.homeHeroTitleGradient || "ماشین‌آلات سنگبری و معدن")}
              </span>
            </h1>

            <p 
              className="text-slate-300 leading-relaxed font-medium max-w-2xl border-r-4 border-blue-500 pr-6 py-2" 
              style={{ 
                borderRight: isEn ? "none" : "4px solid #3b82f6", 
                borderLeft: isEn ? "4px solid #3b82f6" : "none", 
                paddingRight: isEn ? "0" : "1.25rem", 
                paddingLeft: isEn ? "1.25rem" : "0",
                fontSize: settings.homeHeroSubtitleSize ? `clamp(14px, 4vw, ${settings.homeHeroSubtitleSize}px)` : 'clamp(14px, 4vw, 18px)'
              }}
            >
              {isEn 
                ? (settings.homeHeroSubtitleEn || "Almas Sarvari delivers premium machinery with AI-driven precision and worldwide support.") 
                : (settings.homeHeroSubtitle || "شرکت تجارتی الماس سروری با بیش از دو دهه تجربه، پیشگام در واردات و عرضه مدرن‌ترین تجهیزات سنگبری و کفسابی در افغانستان.")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-8 md:pt-16">
              <button 
                onClick={() => navToStore()}
                className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-2xl font-black text-sm md:text-base shadow-2xl shadow-blue-500/20 transition-all flex items-center justify-center gap-3 hover:-translate-y-1 active:scale-95 cursor-pointer"
              >
                <Store className="w-5 h-5 md:w-6 md:h-6" />
                <span>{isEn ? "Browse Products Online" : "مشاهده محصولات به صورت آنلاین"}</span>
                {isEn ? <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" /> : <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform" />}
              </button>
              
              {/* Modern Search Bar - Now fully responsive */}
              <div className="relative flex-1 max-w-lg group">
                <div className="absolute inset-0 bg-blue-500/20 blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity" />
                <div className="relative h-12 md:h-14">
                  <input 
                    type="text" 
                    placeholder={isEn ? "Search SKU..." : "جستجوی هوشمند..."} 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                    className="w-full h-full px-6 md:px-8 bg-white/5 border border-white/10 rounded-2xl text-white backdrop-blur-2xl focus:bg-white/10 focus:border-blue-400/40 focus:outline-none transition-all placeholder:text-slate-500 text-xs md:text-sm font-bold" 
                  />
                  <div className="absolute top-1/2 -translate-y-1/2 bg-blue-600 p-2 rounded-xl shadow-lg cursor-pointer hover:bg-blue-500 transition-colors" style={{ left: isEn ? 'auto' : '0.5rem', right: isEn ? '0.5rem' : 'auto' }} onClick={() => navToStore()}>
                    <Search className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Search Results Dropdown */}
                <AnimatePresence>
                  {isSearchFocused && searchQuery.trim() !== "" && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 right-0 mt-4 bg-white/95 backdrop-blur-2xl rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-slate-100 overflow-hidden z-[100] text-right"
                    >
                      {searchResults.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {searchResults.map((product) => (
                            <div
                              key={product.id}
                              className="p-4 hover:bg-blue-50/80 cursor-pointer flex items-center justify-between transition-all group/res"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                navToStore();
                                setTimeout(() => window.dispatchEvent(new CustomEvent('openProductModal', { detail: product.sku })), 100);
                              }}
                            >
                              <div className="flex items-center gap-4">
                                <img src={product.image} alt={product.name} className="w-12 h-12 rounded-2xl object-cover border border-slate-200" />
                                <div>
                                  <p className="text-sm font-black text-slate-800 group-hover/res:text-blue-600">{isEn && product.specs?.nameEn ? product.specs.nameEn : product.name}</p>
                                  <p className="text-[11px] text-slate-500 font-bold mt-0.5">{product.category.join("، ")}</p>
                                </div>
                              </div>
                              <div className="bg-slate-100 text-slate-500 px-3 py-1.5 rounded-xl text-[10px] font-black group-hover/res:bg-blue-100 group-hover/res:text-blue-600 transition-colors">
                                {product.sku}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-sm text-slate-500 font-bold">
                          {isEn ? "No matching products found" : "کالایی با این مشخصات یافت نشد"}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: CATEGORY NAVIGATOR GRID */}
      <section className="relative z-30 -mt-24 pb-20 max-w-7xl mx-auto w-full px-2 md:px-6">
        <h2 className="sr-only">{isEn ? "Product Categories" : "دسته‌بندی محصولات"}</h2>
        <div className="grid grid-cols-4 gap-2 md:gap-6">
          {homeCategories.map((cat) => (
            <motion.div 
              key={cat}
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navToStore(cat)}
              className="bg-white rounded-xl md:rounded-[2rem] p-2 md:p-6 shadow-2xl shadow-slate-200/50 border border-slate-100 flex flex-col items-center text-center cursor-pointer group"
            >
              <div className="w-8 h-8 md:w-16 md:h-16 bg-blue-50 text-blue-600 rounded-lg md:rounded-2xl flex items-center justify-center mb-2 md:mb-5 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 shadow-inner">
                {/* Scale the icon for mobile */}
                <div className="scale-[0.6] md:scale-100">
                  {getCategoryIcon(cat)}
                </div>
              </div>
              <h3 className="font-black text-slate-800 text-[8px] md:text-sm leading-tight md:leading-relaxed line-clamp-2 px-0.5 uppercase tracking-tighter md:tracking-tight">
                {isEn ? (settings.categoriesEn?.[cat] || cat) : cat}
              </h3>
              <div className="mt-2 md:mt-4 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
                <div className="h-1 w-8 bg-blue-500 rounded-full mx-auto" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECTION 3: CORE SERVICES (Fixed, Premium Style) */}
      <section className="py-10 md:py-20 bg-slate-50/50 relative">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-10">
            {[
              { 
                icon: <Settings />, 
                title: isEn ? "Project Engineering" : "مهندسی و نصب پروژه", 
                desc: isEn ? "Turnkey solutions for industrial stone processing factories." : "طراحی و اجرای کامل خطوط تولید سنگبری، اسلب و تایل با استانداردهای روز دنیا.",
                color: "blue"
              },
              { 
                icon: <CheckCircle2 />, 
                title: isEn ? "Warranty & Quality" : "تضمین و کیفیت الماس", 
                desc: isEn ? "Genuine spare parts and international warranty on all units." : "ارائه ضمانت‌نامه کتبی برای تمامی ماشین‌آلات سنگین و قطعات مصرفی وارداتی.",
                color: "cyan"
              },
              { 
                icon: <PenTool />, 
                title: isEn ? "Technical Support" : "پشتیبانی و قطعات", 
                desc: isEn ? "24/7 technical assistance and ready-to-ship components." : "تامین فوری تیغه‌های الماسه، سگمنت و لقمه‌های کفسابی در سراسر منطقه.",
                color: "indigo"
              }
            ].map((service, i) => (
              <div key={i} className="bg-white p-6 md:p-10 rounded-2xl md:rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/20 group hover:border-blue-200 transition-all text-center flex flex-col items-center">
                <div className={`w-12 h-12 md:w-20 md:h-20 bg-${service.color}-50 text-${service.color}-600 rounded-xl md:rounded-3xl flex items-center justify-center mb-4 md:mb-8 group-hover:rotate-12 transition-all duration-500 shadow-inner`}>
                  {React.cloneElement(service.icon as React.ReactElement<any>, { className: "w-6 h-6 md:w-10 md:h-10" })}
                </div>
                <h3 className="text-lg md:text-2xl font-black text-slate-800 mb-2 md:mb-4">{service.title}</h3>
                <p className="text-xs md:text-sm text-slate-500 leading-relaxed font-medium">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: FEATURED PRODUCTS (If enabled) */}
      {(settings.showFeaturedProducts !== false && featuredProducts.length > 0) && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 space-y-12">
            <div className="flex flex-col md:flex-row justify-between items-end gap-6 border-b-2 border-slate-100 pb-8">
              <div className="text-start space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                    <Star className="w-6 h-6 text-white" fill="currentColor" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tighter">
                    {isEn ? "Advanced Machinery" : "ماشین‌آلات ویژه و پیشنهادی"}
                  </h2>
                </div>
                <p className="text-slate-500 font-bold text-sm">برگزیده برترین تکنولوژی‌های استخراج و فرآوری سنگ</p>
              </div>
              <button onClick={() => navToStore()} className="px-6 py-3 bg-slate-900 hover:bg-blue-600 text-white rounded-2xl font-black text-xs transition-all flex items-center gap-2">
                <span>{isEn ? "View Collection" : "مشاهده کاتالوگ کامل"}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex overflow-x-auto gap-8 pb-10 pt-4 px-2 no-scrollbar snap-x snap-mandatory">
              {featuredProducts.map((p) => {
                const isInCart = quoteCart.some((item) => item.id === p.id);
                return (
                  <motion.div key={p.id} className="snap-center shrink-0 w-[300px] md:w-[360px]">
                    <ProductCard
                      product={p}
                      currency={currency}
                      onAddToQuote={onAddToQuote}
                      isInQuote={isInCart}
                      appLanguage={appLanguage}
                      enableQuoteCart={settings.enableQuoteCart !== false}
                      settings={settings}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 4.5: CONSUMABLES SPOTLIGHT */}
      <section className="py-12 md:py-20 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" />
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="space-y-6 md:space-y-8 text-start">
               <div className="inline-block px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-[9px] md:text-[10px] font-black tracking-widest border border-blue-500/30 uppercase">
                  Consumables & Spare Parts
               </div>
               <h2 className="text-2xl md:text-5xl font-black leading-tight" dir={isEn ? "ltr" : "rtl"}>
                 {isEn ? "Supplying Spare Parts & " : "تامین قطعات یدکی و "}
                 <br className="hidden md:block"/> 
                 <span className="text-blue-400">{isEn ? "Stone Cutting Consumables" : "مواد مصرفی سنگبری"}</span>
               </h2>
               <p className="text-slate-500 text-sm md:text-lg leading-relaxed font-medium" dir={isEn ? "ltr" : "rtl"}>
                 {isEn 
                   ? "Almas Soroori holds the largest inventory of consumable parts including marble and granite diamond blades, advanced segments, and floor polishing abrasives." 
                   : "الماس سروری بزرگترین انبار قطعات مصرفی از جمله تیغه‌های الماسه ماربل و گرانیت، سگمنت‌های پیشرفته و لقمه‌های کفسابی را در اختیار دارد."}
               </p>
               <div className="grid grid-cols-2 gap-4 md:gap-6 pt-2" dir={isEn ? "ltr" : "rtl"}>
                  <div className="flex items-center gap-2 md:gap-3">
                     <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-emerald-500 shrink-0" />
                     <span className="text-xs md:text-sm font-bold text-slate-200">{isEn ? "Immediate Delivery" : "تحویل فوری قطعات"}</span>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                     <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-emerald-500 shrink-0" />
                     <span className="text-xs md:text-sm font-bold text-slate-200">{isEn ? "Authenticity Guarantee" : "تضمین اصالت برند"}</span>
                  </div>
               </div>
               <button
                  onClick={() => {
                    setCurrentTab("store");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="w-full sm:w-auto px-8 py-3.5 md:py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-sm transition-all shadow-lg shadow-blue-500/20 cursor-pointer text-center"
               >
                  {isEn ? "View Product List" : "مشاهده لیست محصولات"}
               </button>
            </div>
            <div className="relative grid grid-cols-2 gap-3 md:gap-4 mt-8 lg:mt-0">
               <div className="space-y-3 md:space-y-4 pt-8 md:pt-12">
                  <div className="aspect-square rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-slate-800 flex items-center justify-center">
                     <Settings className="w-10 h-10 md:w-16 md:h-16 text-emerald-500/50" />
                  </div>
                  <div className="aspect-square rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                     <Cpu className="w-10 h-10 md:w-16 md:h-16 text-white/50" />
                  </div>
               </div>
               <div className="space-y-3 md:space-y-4">
                  <div className="aspect-square rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-slate-800 flex items-center justify-center">
                     <Wrench className="w-10 h-10 md:w-16 md:h-16 text-blue-500/50" />
                  </div>
                  <div className="aspect-square rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-slate-800 flex items-center justify-center">
                     <Cog className="w-10 h-10 md:w-16 md:h-16 text-rose-500/50" />
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST BLOG POSTS */}
      {(posts || []).length > 0 && (
        <section className="py-20 md:py-28 bg-white relative z-20 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest border border-blue-100">
                  <MessageSquare className="w-3 h-3" />
                  {isEn ? (settings.landingBlogLabelEn || "Industrial Insights") : (settings.landingBlogLabel || "آکادمی و وبلاگ تخصصی")}
                </div>
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
                  {isEn 
                    ? (settings.landingBlogTitleEn || "Latest Technical Articles & Training") 
                    : (settings.landingBlogTitle || "آخرین مقالات و آموزش‌های تخصصی")}
                </h2>
              </div>
              <a 
                href="/blog"
                onClick={(e) => { e.preventDefault(); setCurrentTab("blog"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="group flex items-center gap-3 text-slate-500 hover:text-blue-600 font-black text-sm transition-all cursor-pointer"
              >
                {isEn ? "View All Articles" : "مشاهده تمام مقالات"}
                <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center group-hover:border-blue-600 group-hover:bg-blue-50 transition-all">
                   <ArrowRight className="w-4 h-4" />
                </div>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {posts.filter(p => p.isPublished).slice(0, 3).map((post, idx) => (
                <motion.a 
                  key={post.id}
                  href={`/blog?slug=${post.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={(e) => { e.preventDefault(); setCurrentTab("blog"); window.history.pushState({}, "", `/blog?slug=${post.slug}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="group cursor-pointer flex flex-col h-full"
                >
                  <div className="aspect-[16/10] rounded-[2.5rem] overflow-hidden bg-slate-100 mb-6 relative shadow-2xl shadow-slate-200/50 border border-slate-100">
                    {post.image ? (
                      <img src={getOptimizedImageUrl(post.image, 400)} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <MessageSquare className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="space-y-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                       <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(post.createdAt).toLocaleDateString(isEn ? 'en-US' : 'fa-IR')}
                       </div>
                       <div className="w-1 h-1 rounded-full bg-slate-200" />
                       <div className="text-blue-500">{isEn ? "Article" : "مقاله فنی"}</div>
                    </div>
                    <h3 className="text-xl font-black text-slate-800 line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors">
                      {isEn ? (post.titleEn || post.title) : post.title}
                    </h3>
                    <p className="text-sm text-slate-500 font-medium leading-relaxed line-clamp-3">
                      {isEn ? (post.excerptEn || post.excerpt) : post.excerpt}
                    </p>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARTNERS MARQUEE */}
      {(settings.partnerBrands || []).length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-200/60 relative z-20 text-center">
           <div className="max-w-7xl mx-auto px-4">
              <h4 className="text-xs md:text-sm font-black text-slate-950 uppercase tracking-[0.2em] mb-10">
                {isEn 
                  ? (settings.partnerBannerTitleEn || "Strategic Global Partners") 
                  : (settings.partnerBannerTitle || "تأمین‌کنندگان و شرکای استراتژیک بین‌المللی")}
              </h4>
              <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-700">
                 {(settings.partnerBrands || []).map(p => (
                   <div key={p} className="flex items-center gap-2">
                     <span className="text-2xl md:text-4xl font-black text-slate-800 tracking-tighter opacity-100">{p}</span>
                   </div>
                 ))}
              </div>
           </div>
        </section>
      )}

      {/* SECTION 6: FINAL CALL TO ACTION & CATALOG */}
      <section className="py-16 md:py-24 px-4 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900 to-[#0b1120] rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-12 border border-slate-800">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-blue-600/20 blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-cyan-500/20 blur-[100px] pointer-events-none" />
            
            {/* Content */}
            <div className="relative z-10 flex-1 space-y-6 text-center lg:text-right">
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight tracking-tight">
                {isEn ? "Download Our Digital Catalog" : "دریافت کاتالوگ جامع صنعتی"}
              </h2>
              <p className="text-slate-300 text-sm md:text-base leading-relaxed font-medium max-w-2xl mx-auto lg:mx-0">
                {isEn 
                  ? "Get access to full technical data sheets, HD images, and production capacity for all our industrial stone and mining units." 
                  : "مشخصات فنی کامل، ابعاد دقیق و ظرفیت تولید تمامی ماشین‌آلات سنگبری و تجهیزات معادن را در قالب کاتالوگ دیجیتال الماس سروری دریافت کنید."}
              </p>
            </div>

            {/* Actions */}
            <div className="relative z-10 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4 w-full lg:w-auto shrink-0">
              {settings.catalogPdfUrl && (
                <a 
                  href={settings.catalogPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-sm shadow-lg shadow-blue-600/30 transition-all flex items-center justify-center gap-3 cursor-pointer"
                >
                  <Package className="w-5 h-5 shrink-0" />
                  <span>{isEn ? "View Online & Download PDF" : "مشاهده آنلاین و دانلود PDF"}</span>
                </a>
              )}
              <button 
                onClick={() => {
                  setCurrentTab("contact");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }} 
                className="w-full sm:w-auto px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl font-black text-sm transition-all flex items-center justify-center gap-3 cursor-pointer"
              >
                <span>{isEn ? "Talk to an Expert" : "مشاوره فنی پیش از خرید"}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
